import * as React from 'react';
import {
    ShowButton,
    CloneButton,
    EditActions as RaEditActions,
    EditActionsProps,
    TopToolbar,
    usePermissions,
    useResourceContext,
    useResourceDefinition,
    useEditContext,
} from 'react-admin';

import { canAccess } from '../canAccess';

/**
 * Replacement for react-admin's EditAction that adds RBAC control to actions
 *
 * Users must have the 'show' permission on the resource and record to see the ShowButton.
 * Users must have the 'clone' permission on the resource and record to see the CloneButton.
 *
 * @example
 * import { Edit } from 'react-admin';
 * import { EditActions } from '@react-admin/ra-rbac';
 *
 * export const PostEdit = () => (
 *     <Edit actions={<EditActions />}>
 *         ...
 *     </Edit>
 * );
 */
export const EditActions = (props: EditActionsProps) => {
    const { className } = props;
    const { record, ...rest } = useEditContext(props);
    const { hasShow } = useResourceDefinition(props);
    const resource = useResourceContext(props);
    const { isLoading, permissions } = usePermissions();
    if (isLoading) {
        return null;
    }
    const canShow =
        hasShow &&
        canAccess({
            permissions,
            action: 'show',
            resource,
            record,
        });
    const canClone = canAccess({
        permissions,
        action: 'clone',
        resource,
        record,
    });
    if (!canShow && !canClone) {
        return <div style={{ marginTop: '1em' }} />;
    }
    return (
        <TopToolbar className={className} {...sanitizeRestProps(rest)}>
            {canShow && <ShowButton />}
            {canClone && <CloneButton />}
        </TopToolbar>
    );
};

EditActions.propTypes = RaEditActions.propTypes;

const sanitizeRestProps = ({
    basePath,
    defaultTitle,
    className,
    hasCreate,
    hasList,
    hasEdit,
    isFetching,
    isLoading,
    mutationMode,
    refetch,
    registerMutationMiddleware,
    unregisterMutationMiddleware,
    resource,
    save,
    saving,
    ...rest
}: any) => rest;
